import {
  type AskPunttAttachment,
  type AskPunttMessage,
} from "@mg/schemas/src/christo/askPuntt";

import {
  type ChatArtifact,
  type ChatArtifactKind,
  type ChatArtifactSelection,
} from "./artifactTypes";
import { type ConnectorOption } from "./components/InputPlusMenu";
import { referenceDocumentIdentity } from "./utils/projectContextDocuments";

const CANVAS_OUTPUT_TYPE_LABELS: Record<string, string> = {
  risk_assessment: "Risk Assessment",
  brief_document: "Creative Brief",
  comparison: "Comparison",
  report: "Report",
  visualization: "Visualization",
  custom: "Document",
};

// Reduce a URL to its pathname so origin/signature/fragment differences do
// not prevent a match. Falls back to the trimmed input when URL parsing
// fails (e.g. relative paths).
function urlPathname(url: string | undefined | null): string {
  if (!url) return "";
  try {
    return new URL(url).pathname;
  } catch {
    const q = url.indexOf("?");
    const h = url.indexOf("#");
    const end = Math.min(q >= 0 ? q : url.length, h >= 0 ? h : url.length);
    return url.slice(0, end);
  }
}

// Resolve a citation's source URL to an index in the context file list. Signed
// S3 URLs are compared by pathname because the query-string signature can
// differ between when the backend emitted the citation and when the frontend
// fetched the asset. Returns -1 when nothing matches (caller keeps the current
// file selection).
export function findContextFileIndex(
  contextFiles: readonly { source: string }[],
  sourceUrl: string | undefined,
  originalURL: string | undefined,
): number {
  const sourceKey = urlPathname(sourceUrl);
  const originalKey = urlPathname(originalURL);
  if (!sourceKey && !originalKey) return -1;
  for (let i = 0; i < contextFiles.length; i++) {
    const fileKey = urlPathname(contextFiles[i]?.source);
    if (!fileKey) continue;
    if (fileKey === sourceKey || fileKey === originalKey) return i;
  }
  // Identity fallback: the AI cites the office ORIGINAL (…-context.docx) while
  // the board stores samba's converted PDF (…-context.pdf). Exact-pathname
  // matching can't see they are the same upload, so resolve by reference-
  // document identity (basename stem minus "-context"). The converted PDF is
  // the representation carrying the citation's coordinate space, so the pin
  // renders there — the Microsoft Office viewer cannot place one at all.
  const sourceIdentity = sourceUrl ? referenceDocumentIdentity(sourceUrl) : "";
  const originalIdentity = originalURL
    ? referenceDocumentIdentity(originalURL)
    : "";
  if (!sourceIdentity && !originalIdentity) return -1;
  for (let i = 0; i < contextFiles.length; i++) {
    const fileSource = contextFiles[i]?.source;
    if (!fileSource) continue;
    const fileIdentity = referenceDocumentIdentity(fileSource);
    if (!fileIdentity) continue;
    if (fileIdentity === sourceIdentity || fileIdentity === originalIdentity) {
      return i;
    }
  }
  return -1;
}

/** Minimal file shape the context pane can render (structural AssetFile). */
export type CitableFile = {
  source: string;
  name?: string;
  contentType?: string;
};

// Extensions AssetViewerPane can route to a viewer (pdf/image/spreadsheet/
// office/video). Off-ticket citation URLs without one of these would mount a
// file the pane cannot render, so they are skipped.
const CITABLE_EXTENSION_RE =
  /\.(pdf|ai|jpg|jpeg|png|gif|webp|svg|bmp|xls|xlsx|csv|ods|ppt|pptx|doc|docx|mp4|mov|avi|wmv|flv|mkv|m3u8)$/i;

function citedFileName(pathname: string): string | undefined {
  const base = pathname.split("/").pop() ?? "";
  try {
    return decodeURIComponent(base) || undefined;
  } catch {
    return base || undefined;
  }
}

/**
 * Merge the board's context files with every additional document the review
 * comments cite, so the context pane can resolve ANY file citation:
 *
 *  - Self-citations: the AI cites another page of the reviewed document
 *    itself (single-file proof decks cite their own spec page). The matching
 *    review file is appended so `findContextFileIndex` resolves it.
 *  - Off-ticket citations: the AI cites a shared enterprise document (e.g. a
 *    brand guideline) that is not attached to the ticket at all. A minimal
 *    viewable entry is synthesized from the citation URL.
 *
 * Matching is by URL pathname (signed-URL query strings differ between when
 * the citation was emitted and when assets were fetched). Returns the input
 * `contextFiles` reference untouched when the citations add nothing, so
 * consumers keyed on array identity (viewer scroll reset) stay stable.
 */
export function buildCitableContextFiles<T extends { source: string }>(
  contextFiles: T[],
  reviewFiles: T[],
  comments: readonly { citations?: unknown }[] | undefined,
): (T | CitableFile)[] {
  const known = new Set(
    contextFiles.map((f) => urlPathname(f.source)).filter(Boolean),
  );
  // Reference-document identities of the board's context files (basename stem
  // minus "-context"). Lets us recognize a citation that points at the office
  // ORIGINAL (…-context.docx) as the same upload as the converted PDF
  // (…-context.pdf) already in contextFiles, so we don't append a second
  // renderable document that shows the same source twice (PDF + Office viewer).
  const knownIdentities = new Set(
    contextFiles
      .map((f) => referenceDocumentIdentity(f.source))
      .filter(Boolean),
  );
  const additions: (T | CitableFile)[] = [];

  for (const comment of comments ?? []) {
    const citations = Array.isArray(comment?.citations)
      ? comment.citations
      : [];
    for (const citation of citations) {
      if (citation == null || typeof citation !== "object") continue;
      const { url, originalURL } = citation as {
        url?: unknown;
        originalURL?: unknown;
      };
      const urlKey = urlPathname(typeof url === "string" ? url : "");
      const originalKey = urlPathname(
        typeof originalURL === "string" ? originalURL : "",
      );
      if (!urlKey && !originalKey) continue;
      if (
        (urlKey && known.has(urlKey)) ||
        (originalKey && known.has(originalKey))
      ) {
        continue;
      }
      // Same logical upload as a context file already present (office original
      // vs its converted PDF) — findContextFileIndex's identity fallback
      // resolves the citation to that PDF, so skip rather than append a phantom
      // duplicate the pane would render in a second (Microsoft Office) viewer.
      const urlIdentity = referenceDocumentIdentity(
        typeof url === "string" ? url : "",
      );
      const originalIdentity = referenceDocumentIdentity(
        typeof originalURL === "string" ? originalURL : "",
      );
      if (
        (urlIdentity && knownIdentities.has(urlIdentity)) ||
        (originalIdentity && knownIdentities.has(originalIdentity))
      ) {
        continue;
      }

      const reviewMatch = reviewFiles.find((f) => {
        const fileKey = urlPathname(f.source);
        return (
          fileKey !== "" && (fileKey === urlKey || fileKey === originalKey)
        );
      });
      if (reviewMatch) {
        additions.push(reviewMatch);
      } else {
        const source = typeof url === "string" && url ? url : "";
        const key = urlKey || originalKey;
        // Only synthesize entries the viewer can actually render.
        if (!source || !CITABLE_EXTENSION_RE.test(key)) continue;
        additions.push({ source, name: citedFileName(key) });
      }
      if (urlKey) known.add(urlKey);
      if (originalKey) known.add(originalKey);
      if (urlIdentity) knownIdentities.add(urlIdentity);
      if (originalIdentity) knownIdentities.add(originalIdentity);
    }
  }

  return additions.length ? [...contextFiles, ...additions] : contextFiles;
}

/**
 * Extract the chronologically-ordered list of unique review ticket IDs from
 * chat messages. Drives the "Review N of M" navigation when a single chat
 * has multiple sequential reviews.
 *
 * Only considers assistant messages with a viewTicketId. The first occurrence
 * of each ticketId wins so order is stable even if a ticket is referenced
 * again later in the conversation.
 */
export function collectReviewTicketIds(
  messages: readonly { role: string; viewTicketId?: string | null }[],
): string[] {
  const seen = new Set<string>();
  const ids: string[] = [];
  for (const msg of messages) {
    if (
      msg.role === "assistant" &&
      msg.viewTicketId &&
      !seen.has(msg.viewTicketId)
    ) {
      seen.add(msg.viewTicketId);
      ids.push(msg.viewTicketId);
    }
  }
  return ids;
}

/**
 * Whether a searchProgress `source` is the create-review tool chip.
 * Accepts legacy `"Create Ticket"` (pre-rename chats) and `"Create Asset"`.
 */
export function isCreateAssetProgressSource(
  source: string | undefined | null,
): boolean {
  return source === "Create Asset" || source === "Create Ticket";
}

/**
 * Map ticket ids to display titles from create-asset search-progress
 * events in the chat transcript. Used by the standalone multi-review rail.
 */
export function collectReviewTicketTitles(
  messages: readonly {
    searchProgress?: { source?: string; results?: unknown[] }[];
  }[],
): Map<string, string> {
  const titleMap = new Map<string, string>();
  for (const msg of messages) {
    for (const sp of msg.searchProgress ?? []) {
      if (isCreateAssetProgressSource(sp.source)) {
        for (const r of (sp.results ?? []) as {
          id?: string;
          title?: string;
        }[]) {
          if (r.id && r.title) titleMap.set(r.id, r.title);
        }
      }
    }
  }
  return titleMap;
}

/**
 * Chronological list of unique files the user uploaded in this chat (deduped
 * by attachment key or source URL). Standalone chats persist uploads on user
 * messages — no separate project-files API.
 */
export function collectChatUploadAttachments(
  messages: readonly {
    role: string;
    attachments?: AskPunttAttachment[] | null;
  }[],
): AskPunttAttachment[] {
  const seen = new Set<string>();
  const out: AskPunttAttachment[] = [];
  for (const msg of messages) {
    if (msg.role !== "user") continue;
    for (const att of msg.attachments ?? []) {
      const dedupeKey = att.key?.trim() || att.source?.trim();
      if (!dedupeKey || seen.has(dedupeKey)) continue;
      seen.add(dedupeKey);
      out.push(att);
    }
  }
  return out;
}

/**
 * Whether this is the chat's FIRST review — i.e. it has produced at most one
 * review ticket so far.
 *
 * A single-asset review produces exactly one `viewTicketId` (the in-flight
 * first review counts as 1). A *prior* completed review leaves its own ticket,
 * so two or more distinct ticket ids means a review has already finished before
 * the current one.
 *
 * IMPORTANT: we count tickets rather than "any viewTicketId exists" because
 * `viewTicketId` is stamped on the review-triggering assistant message BEFORE
 * `chat.reviewStatus` turns on (the "limbo" window — see `isReviewInLimbo` in
 * chat.tsx). So "any viewTicketId" would already be true during the first
 * review and the suppression would never fire. Counting tickets sidesteps that
 * timing entirely.
 */
export function isFirstReviewForChat(
  reviewTicketIds: readonly string[],
): boolean {
  return reviewTicketIds.length <= 1;
}

/**
 * Whether the right pane should be fully suppressed (chat-only, full-width)
 * because a brand-new single-asset chat is running its first review.
 *
 * Scope (intentional):
 *   - Single-asset, non-project reviews only. Project / multi-asset / killer-
 *     flow chats are EXCLUDED — they keep their canvas + project info visible
 *     during reviews. A chat is "project-flavored" if it has
 *     `workflowType === "project"`, carries `projectData` (`activeProjectData`),
 *     is in the project-loading window, OR is mid killer-flow typewriter
 *     (`isKillerFlowStreaming`). The killer-flow guard matters because the
 *     demo emits `projectData` slightly AFTER the typewriter starts (christo
 *     deploy lag), so without it a demo review would flash chat-only in that
 *     gap.
 *   - Only a "brand-new" chat — one with no canvas artifact yet
 *     (`hasCanvasContent` false). A chat that already produced a canvas (e.g.
 *     an ROI breakdown) and then runs a review is NOT brand-new; blanking that
 *     canvas to go chat-only would hide context the user is actively looking
 *     at, so the right pane is kept and canvas-vs-ticket precedence applies.
 *   - Only the first review (the chat has produced at most one review ticket).
 *   - A stalled review (status held past the stall threshold AND an assistant
 *     reply has already landed) reveals the pane, so a leaked/stuck
 *     `reviewStatus` can never hide findings forever.
 *
 * The `reviewInLimbo` signal closes the gap at the START of a review:
 * rembrandt stamps `viewTicketId` on the assistant message (step 4) BEFORE it
 * sets `chat.reviewStatus` (step 5, ~45s+ later). During that window
 * `isReviewing` is still false but `effectiveViewTicketId` is already set, so
 * without folding limbo in the ticket pane flashes the instant the asset is
 * uploaded — before any findings exist. Treating limbo as "reviewing" keeps
 * the pane suppressed from upload through the entire review; it self-clears the
 * moment the post-review summary lands (no `viewTicketId` on it) or the 15-min
 * limbo cap fires (see `isReviewInLimbo` in chat.tsx).
 */
export function shouldHideRightPaneForFirstReview(args: {
  isProjectChat: boolean;
  hasActiveProjectData: boolean;
  isProjectLoading: boolean;
  isKillerFlowStreaming: boolean;
  hasCanvasContent: boolean;
  isReviewing: boolean;
  reviewInLimbo: boolean;
  reviewStalled: boolean;
  isFirstReview: boolean;
}): boolean {
  const isSingleAssetReview =
    !args.isProjectChat &&
    !args.hasActiveProjectData &&
    !args.isProjectLoading &&
    !args.isKillerFlowStreaming &&
    !args.hasCanvasContent;
  return (
    isSingleAssetReview &&
    (args.isReviewing || args.reviewInLimbo) &&
    args.isFirstReview &&
    !args.reviewStalled
  );
}

/**
 * Extract the chronologically-ordered list of unique assistant message IDs
 * that carry a non-empty canvas (≥1 block). Drives the "Canvas N of M" style
 * navigation when a single chat has multiple sequential canvases — the
 * canvas analogue of `collectReviewTicketIds`.
 *
 * First occurrence of each message id wins so order is stable.
 */
export function collectCanvasMessageIds(
  messages: readonly {
    _id: string;
    role: string;
    canvasContent?: { blocks?: unknown[] } | null;
  }[],
): string[] {
  const seen = new Set<string>();
  const ids: string[] = [];
  for (const msg of messages) {
    if (
      msg.role === "assistant" &&
      msg.canvasContent &&
      Array.isArray(msg.canvasContent.blocks) &&
      msg.canvasContent.blocks.length > 0 &&
      !seen.has(msg._id)
    ) {
      seen.add(msg._id);
      ids.push(msg._id);
    }
  }
  return ids;
}

type CanvasMessageForTitle = {
  activeSkill?: string | null;
  canvasContent?: {
    outputType?: string;
    blocks?: { type?: string; text?: string; content?: string }[];
  } | null;
};

/**
 * Derive a human-readable sidebar title for a canvas-bearing message.
 * Falls back: activeSkill label -> first heading block -> outputType label.
 */
export function artifactTitleForCanvas(message: CanvasMessageForTitle): string {
  if (message.activeSkill) {
    const skillLabel = ASK_PUNTT_SKILL_LABELS[message.activeSkill];
    if (skillLabel) return skillLabel;
  }
  const blocks = message.canvasContent?.blocks ?? [];
  for (const block of blocks) {
    if (block.type === "heading") {
      const text = (block.text ?? block.content ?? "").trim();
      if (text) return text.length > 60 ? `${text.slice(0, 60)}...` : text;
    }
  }
  const outputType = message.canvasContent?.outputType ?? "custom";
  return CANVAS_OUTPUT_TYPE_LABELS[outputType] ?? "Document";
}

export function canvasSubtitleForMessage(
  message: CanvasMessageForTitle,
): string {
  const outputType = message.canvasContent?.outputType;
  if (!outputType) return "Document";
  return CANVAS_OUTPUT_TYPE_LABELS[outputType] ?? outputType;
}

export type CollectChatArtifactsMessage = {
  _id: string;
  role: string;
  createdAt?: string;
  viewTicketId?: string | null;
  canvasContent?: {
    outputType?: string;
    blocks?: unknown[];
  } | null;
  canvasLoading?: boolean | null;
  activeSkill?: string | null;
  status?: string;
};

export type ReviewSummaryForArtifact = {
  thumbnailUrl?: string | null;
  aiCommentCount?: number;
  humanCommentCount?: number;
  versionNumber?: number;
  /** True while the ticket pipeline is still running (not yet viewable). */
  isReviewInFlight?: boolean;
};

type TicketBoardLike = {
  isRunningAIReview?: boolean;
  isPending?: boolean;
  isAIReviewed?: boolean;
  deletedAt?: unknown;
};

type TicketLikeForReviewStatus = {
  aiStatus?: string;
  revisionBoards?: TicketBoardLike[];
};

/** True when every ticket in the chat has a loaded, completed review summary. */
export function areAllReviewTicketsComplete(
  ticketIds: readonly string[],
  summaries: readonly { ticketId: string; isReviewInFlight: boolean }[],
): boolean {
  if (ticketIds.length === 0) return false;
  return ticketIds.every((id) => {
    const summary = summaries.find((s) => s.ticketId === id);
    return summary != null && !summary.isReviewInFlight;
  });
}

/** Mirrors project feed `deriveTicketReviewStatus` for chat review cards. */
export function deriveReviewInFlightFromTicket(
  ticket: TicketLikeForReviewStatus | null | undefined,
): boolean {
  if (!ticket) return true;
  const boards = (ticket.revisionBoards ?? []).filter((b) => !b?.deletedAt);
  const board = boards.at(-1);
  if (board?.isRunningAIReview || board?.isPending) return true;
  if (board?.isAIReviewed) return false;
  if (ticket.aiStatus === "aireviewed") return false;
  return true;
}

/**
 * Merge review tickets and canvas documents into a single chronological
 * artifact list for the cowork-style sidebar. Reviews and canvases interleave
 * by message order; each ticket id and message id appears at most once.
 */
export function collectChatArtifacts(
  messages: readonly CollectChatArtifactsMessage[],
  titleMap: ReadonlyMap<string, string> = new Map(),
  reviewSummaries: ReadonlyMap<string, ReviewSummaryForArtifact> = new Map(),
): ChatArtifact[] {
  const artifacts: ChatArtifact[] = [];
  const seenReviewTickets = new Set<string>();
  const seenCanvasMessages = new Set<string>();

  for (const msg of messages) {
    if (msg.role !== "assistant") continue;

    if (msg.viewTicketId && !seenReviewTickets.has(msg.viewTicketId)) {
      seenReviewTickets.add(msg.viewTicketId);
      const ticketId = msg.viewTicketId;
      const summary = reviewSummaries.get(ticketId);
      const title =
        titleMap.get(ticketId) ?? `Review ${seenReviewTickets.size}`;
      // viewTicketId is stamped before findings exist; wait for ticket summary
      // hydration and a completed review pipeline before enabling open/unread.
      const isGenerating =
        msg.status === "pending" ||
        !reviewSummaries.has(ticketId) ||
        summary?.isReviewInFlight === true;

      artifacts.push({
        id: ticketId,
        kind: "review",
        messageId: msg._id,
        title,
        createdAt: msg.createdAt,
        ticketId,
        thumbnailUrl: summary?.thumbnailUrl ?? null,
        isGenerating,
        versionNumber: summary?.versionNumber,
        aiCommentCount: summary?.aiCommentCount,
        humanCommentCount: summary?.humanCommentCount,
      });
    }

    const hasCanvasBlocks =
      msg.canvasContent &&
      Array.isArray(msg.canvasContent.blocks) &&
      msg.canvasContent.blocks.length > 0;

    if (hasCanvasBlocks && !seenCanvasMessages.has(msg._id)) {
      seenCanvasMessages.add(msg._id);
      artifacts.push({
        id: msg._id,
        kind: "canvas",
        messageId: msg._id,
        title: artifactTitleForCanvas(msg as CanvasMessageForTitle),
        subtitle: canvasSubtitleForMessage(msg as CanvasMessageForTitle),
        createdAt: msg.createdAt,
        outputType: msg.canvasContent?.outputType,
        isGenerating: msg.canvasLoading === true && msg.status !== "completed",
      });
    } else if (
      msg.canvasLoading === true &&
      msg.status !== "completed" &&
      !seenCanvasMessages.has(msg._id)
    ) {
      seenCanvasMessages.add(msg._id);
      artifacts.push({
        id: msg._id,
        kind: "canvas",
        messageId: msg._id,
        title: artifactTitleForCanvas(msg as CanvasMessageForTitle),
        subtitle: "Generating...",
        createdAt: msg.createdAt,
        outputType: msg.canvasContent?.outputType,
        isGenerating: true,
      });
    }
  }

  return artifacts;
}

type ThreadArtifactAnchorMessage = {
  _id: string;
  role: string;
  viewTicketId?: string | null;
  content?: string | null;
  agentSteps?: { tool?: string; type?: string }[];
};

/** Shape match for per-ticket review-status rows (see chat.tsx). */
export function isReviewStatusMessageShape(
  m: ThreadArtifactAnchorMessage,
): boolean {
  return (
    m.role === "assistant" &&
    (m.content ?? "") === "" &&
    (m.agentSteps?.length ?? 0) > 0 &&
    (m.agentSteps ?? []).every((s) => s.tool === "thought" || s.type === "text")
  );
}

/**
 * Resolve where an inline thread artifact card should anchor in the
 * timeline. Canvas/documents stay on the producing message; reviews move
 * to the first post-review summary after their ticket was created.
 */
export function resolveThreadArtifactAnchorMessageId(
  artifact: ChatArtifact,
  messages: readonly ThreadArtifactAnchorMessage[],
): string {
  if (artifact.kind === "canvas") {
    return artifact.messageId;
  }

  const ticketId = artifact.ticketId ?? artifact.id;
  const ticketMsgIdx = messages.findIndex(
    (m) => m.role === "assistant" && m.viewTicketId === ticketId,
  );
  if (ticketMsgIdx < 0) {
    return artifact.messageId;
  }

  for (let i = ticketMsgIdx + 1; i < messages.length; i++) {
    const m = messages[i];
    if (
      m.role === "assistant" &&
      !m.viewTicketId &&
      !isReviewStatusMessageShape(m)
    ) {
      return m._id;
    }
  }

  return artifact.messageId;
}

/** Group artifacts by their inline thread anchor message id. */
export function buildThreadArtifactsByAnchorMessageId(
  artifacts: readonly ChatArtifact[],
  messages: readonly ThreadArtifactAnchorMessage[],
): Map<string, ChatArtifact[]> {
  const map = new Map<string, ChatArtifact[]>();
  for (const artifact of artifacts) {
    const anchorId = resolveThreadArtifactAnchorMessageId(artifact, messages);
    const list = map.get(anchorId) ?? [];
    list.push(artifact);
    map.set(anchorId, list);
  }
  return map;
}

export function selectionMatchesArtifact(
  selection: ChatArtifactSelection | null,
  artifact: ChatArtifact,
): boolean {
  if (!selection) return false;
  if (selection.kind === "review" && artifact.kind === "review") {
    return selection.ticketId === artifact.ticketId;
  }
  if (selection.kind === "canvas" && artifact.kind === "canvas") {
    return selection.messageId === artifact.messageId;
  }
  return false;
}

export function artifactKindLabel(kind: ChatArtifactKind): string {
  return kind === "review" ? "Review" : "Document";
}

/**
 * Build the click handler that activates an older review ticket from the
 * chat history. Extracted into a factory so the index lookup + dismissed
 * clear + analytics payload can be unit-tested directly without mounting
 * `chat.tsx`.
 *
 * Stale ticket ids (e.g. a ticket deleted server-side after the chat
 * loaded) silently no-op: no state mutation, no analytics event. The
 * `TicketViewPane` already renders its own "Ticket not found" state, but
 * we want a stale click to be a non-event from the chat's perspective.
 */
export interface CreateActivateTicketHandlerDeps {
  allReviewTicketIds: readonly string[];
  setSelectedReviewIndex: (idx: number) => void;
  setTicketDismissed: (dismissed: boolean) => void;
  capture: (event: string, props: Record<string, unknown>) => void;
  chatId: string | undefined;
}

export function createActivateTicketHandler(
  deps: CreateActivateTicketHandlerDeps,
): (ticketId: string) => void {
  return (ticketId: string) => {
    const idx = deps.allReviewTicketIds.indexOf(ticketId);
    if (idx < 0) return;
    deps.setSelectedReviewIndex(idx);
    deps.setTicketDismissed(false);
    deps.capture("ask_puntt_previous_artifact_clicked", {
      artifact: "ticket",
      ticket_id: ticketId,
      chat_id: deps.chatId,
    });
  };
}

/**
 * D8 streaming-suppression predicate. Walks the latest assistant message's
 * `agentSteps`, pairing tool_calls with their tool_results positionally
 * per tool name. If at least one unresolved (open) tool_call has a tool
 * name in `artifactProducingTools`, returns true — older clickable artifact
 * rows should grey out so a stale click can't yank the right pane while
 * a new artifact is mid-stream.
 *
 * The check is intentionally narrow: it fires only when the LAST message
 * is an in-flight assistant turn. Completed turns don't block. A turn
 * that calls `puntt_search` (non-artifact) doesn't block.
 */
export function computeIsAnotherArtifactStreaming(
  messages: readonly {
    role: string;
    status?: string;
    agentSteps?: { type: string; tool?: string }[];
  }[],
  artifactProducingTools: ReadonlySet<string>,
): boolean {
  const lastMsg = messages[messages.length - 1];
  if (!lastMsg || lastMsg.role !== "assistant") return false;
  if (lastMsg.status === "completed") return false;
  if (!lastMsg.agentSteps) return false;
  const open = new Map<string, number>();
  for (const step of lastMsg.agentSteps) {
    if (step.type === "tool_call" && step.tool) {
      open.set(step.tool, (open.get(step.tool) ?? 0) + 1);
    } else if (step.type === "tool_result" && step.tool) {
      open.set(step.tool, (open.get(step.tool) ?? 0) - 1);
    }
  }
  let found = false;
  open.forEach((count, tool) => {
    if (count > 0 && artifactProducingTools.has(tool)) found = true;
  });
  return found;
}

/**
 * Canvas counterpart to `createActivateTicketHandler`. Stale message ids
 * (canvas wiped by a backend re-render, message dropped from `localMessages`,
 * etc.) no-op the same way so the click is never a destructive operation.
 */
export interface CreateActivateCanvasHandlerDeps {
  canvasMessageIds: readonly string[];
  setSelectedCanvasMessageId: (id: string | null) => void;
  setCanvasDismissed: (dismissed: boolean) => void;
  capture: (event: string, props: Record<string, unknown>) => void;
  chatId: string | undefined;
}

export function createActivateCanvasHandler(
  deps: CreateActivateCanvasHandlerDeps,
): (messageId: string) => void {
  return (messageId: string) => {
    if (!deps.canvasMessageIds.includes(messageId)) return;
    deps.setSelectedCanvasMessageId(messageId);
    deps.setCanvasDismissed(false);
    deps.capture("ask_puntt_previous_artifact_clicked", {
      artifact: "canvas",
      message_id: messageId,
      chat_id: deps.chatId,
    });
  };
}

/** Convert chat's enabledConnectors array to Record for display */
export function connectorsArrayToRecord(
  ids: string[] | undefined,
  available: ConnectorOption[],
): Record<string, boolean> {
  const record: Record<string, boolean> = {};
  available.forEach((c) => {
    record[c.id] = ids?.includes(c.id) ?? false;
  });
  return record;
}

/**
 * Monotonic merge: never regress append-only arrays or clear fields
 * that haven't been replaced by final content yet. Prevents stale poll
 * responses from overwriting locally accumulated status data.
 */
export type AskPunttMessageExt = AskPunttMessage & {
  canvasChangedSectionIds?: string[];
  projectLoading?: boolean | null;
  // Set by the shared-chat agent when it mutated the project this turn. The
  // campaign workspace refetches the project on it (docs panel + header).
  projectMutated?: boolean | null;
};

/**
 * Display-only messages are read by the agent but excluded from the rendered
 * timeline (e.g. the synthesized transcript a shared private chat is ingested
 * as — the user only wants the "Shared the chat…" feed note plus the agent's
 * outcome turn, not the verbose transcript). Strict `=== true` so an absent or
 * `false` flag always renders.
 */
export const isHiddenMessage = (
  m: Pick<AskPunttMessageExt, "hidden">,
): boolean => m.hidden === true;

/**
 * Prefer the richer projectData object. If incoming is missing or emptier than
 * the local copy, keep local to avoid regressing progressively streamed fields.
 */
export function mergeProjectData(
  local: AskPunttMessage["projectData"],
  incoming: AskPunttMessage["projectData"],
): AskPunttMessage["projectData"] {
  if (!incoming) return local;
  if (!local) return incoming;
  const localKeys = Object.keys(local).filter(
    (k) => (local as Record<string, unknown>)[k] != null,
  );
  const incomingKeys = Object.keys(incoming).filter(
    (k) => (incoming as Record<string, unknown>)[k] != null,
  );
  return incomingKeys.length >= localKeys.length ? incoming : local;
}

/**
 * Prefer the longer agentSteps array while a turn is still streaming
 * (append-only). Once the server marks the message completed, trust its
 * snapshot even when shorter — rembrandt often converts `tool_call` →
 * `tool_result` in place (same length) or drops stale open calls, and
 * keeping a longer local snapshot leaves unpaired `tool_call` chips
 * spinning forever ("Update Chat Details" etc.).
 */
export function preferAgentSteps(
  local: AskPunttMessageExt["agentSteps"],
  incoming: AskPunttMessageExt["agentSteps"],
  incomingStatus: AskPunttMessageExt["status"] | undefined,
): AskPunttMessageExt["agentSteps"] {
  if (incomingStatus === "completed" && incoming !== undefined) {
    return incoming;
  }
  return (incoming?.length ?? 0) >= (local?.length ?? 0) ? incoming : local;
}

export function mergeMessageStatus(
  local: AskPunttMessageExt,
  incoming: AskPunttMessageExt,
): AskPunttMessageExt {
  return {
    ...local,
    agentSteps: preferAgentSteps(
      local.agentSteps,
      incoming.agentSteps,
      incoming.status,
    ),
    searchProgress:
      (incoming.searchProgress?.length ?? 0) >=
      (local.searchProgress?.length ?? 0)
        ? incoming.searchProgress
        : local.searchProgress,
    streamingContent:
      incoming.streamingContent !== null &&
      incoming.streamingContent !== undefined
        ? incoming.streamingContent
        : incoming.content
          ? incoming.streamingContent
          : local.streamingContent,
    content: incoming.content || local.content,
    status:
      local.status === "completed"
        ? "completed"
        : (incoming.status ?? local.status),
    structuredContent: incoming.structuredContent ?? local.structuredContent,
    canvasContent: incoming.canvasContent ?? local.canvasContent,
    canvasLoading: incoming.canvasContent
      ? incoming.canvasLoading
      : (incoming.canvasLoading ?? local.canvasLoading),
    canvasChangedSectionIds:
      incoming.canvasChangedSectionIds ?? local.canvasChangedSectionIds,
    viewTicketId: incoming.viewTicketId ?? local.viewTicketId,
    suggestedResponses: incoming.suggestedResponses ?? local.suggestedResponses,
    userQuestions: incoming.userQuestions ?? local.userQuestions,
    projectData: mergeProjectData(local.projectData, incoming.projectData),
    projectLoading: incoming.projectData
      ? incoming.projectLoading
      : (incoming.projectLoading ?? local.projectLoading),
    contentBlocks: incoming.contentBlocks ?? local.contentBlocks,
    feedback: incoming.feedback ?? local.feedback,
    projectMutated: incoming.projectMutated ?? local.projectMutated,
    mentions:
      incoming.role === "user"
        ? (incoming.mentions ?? local.mentions)
        : local.mentions,
  };
}

export type FeedActorMember = {
  _id: string;
  name?: string;
  email?: string;
  avatar?: string | null;
};

/** True when the signed-in user authored this feed user message. */
export function isFeedMessageFromViewer(
  message: { userId?: string },
  options: { currentUserId?: string; chatOwnerId?: string },
): boolean {
  if (!options.currentUserId) return false;
  const authorId =
    message.userId ?? options.chatOwnerId ?? options.currentUserId;
  return authorId === options.currentUserId;
}

/** Resolve avatar + display name for a user message in project feed presentation. */
export function resolveFeedActorForUserMessage(
  message: { userId?: string },
  options: {
    currentUserId?: string;
    chatOwnerId?: string;
    members?: FeedActorMember[];
    viewerName?: string;
    viewerAvatar?: string | null;
  },
): { name: string; avatar?: string | null } {
  const authorId =
    message.userId ?? options.chatOwnerId ?? options.currentUserId;
  const member = authorId
    ? options.members?.find((m) => m._id === authorId)
    : undefined;

  if (member) {
    return {
      name: member.name ?? member.email ?? "Member",
      avatar: member.avatar ?? undefined,
    };
  }

  // Member list may be stale; use viewer profile only for the signed-in author.
  if (authorId && options.currentUserId && authorId === options.currentUserId) {
    return {
      name: options.viewerName ?? "Member",
      avatar: options.viewerAvatar ?? undefined,
    };
  }

  return { name: "Member", avatar: undefined };
}

/**
 * Sentinel actor id for the AI agent in feed @mentions. Stored on the message
 * `mentions[]` alongside member `_id`s and surfaced as the `@puntt` chip.
 */
export const PUNTT_MENTION_ID = "puntt";

/** An actor (member or @puntt) that can be mentioned in the project feed. */
export type MentionActor = { id: string; name: string };

/** Shared chip styling for @mentions in the feed composer and message bubbles. */
export const MENTION_HIGHLIGHT_CLASS =
  "rounded bg-puntt-accent-3 px-0.5 font-medium text-puntt-accent-11";

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Regex matching an actor's `@token` in message text. `@puntt` matches
 * case-insensitively on a word boundary; members match their exact
 * `@<name>` token (names may contain spaces, so no trailing boundary).
 */
function mentionTokenRegex(actor: MentionActor, global: boolean): RegExp {
  if (actor.id === PUNTT_MENTION_ID) {
    return new RegExp("@puntt\\b", global ? "gi" : "i");
  }
  return new RegExp(`@${escapeRegExp(actor.name)}`, global ? "g" : "");
}

/**
 * Given the composer text and the actors the user picked from the dropdown,
 * return the ids whose `@token` is still present in the text. Deleting a
 * token from the textarea therefore drops its mention from the payload.
 */
export function resolveActiveMentions(
  text: string,
  selected: MentionActor[],
): string[] {
  return getActiveMentionActors(text, selected)
    .filter((a) => a.id !== PUNTT_MENTION_ID && !a.id.startsWith("email:"))
    .map((a) => a.id);
}

/** Split active composer mentions into user ids (incl. puntt) and invite emails. */
export function resolveMentionPayload(
  text: string,
  selected: MentionActor[],
): { mentions: string[]; mentionEmails: string[] } {
  const active = getActiveMentionActors(text, selected);
  const mentions: string[] = [];
  const mentionEmails: string[] = [];
  for (const actor of active) {
    if (actor.id.startsWith("email:")) {
      mentionEmails.push(actor.name);
    } else {
      mentions.push(actor.id);
    }
  }
  return { mentions, mentionEmails };
}

export function emailMentionId(email: string): string {
  return `email:${email.trim().toLowerCase()}`;
}

/** Actors whose `@token` is still present in the composer or message text. */
export function getActiveMentionActors(
  text: string,
  selected: MentionActor[],
): MentionActor[] {
  const actors: MentionActor[] = [];
  for (const actor of selected) {
    if (actors.some((a) => a.id === actor.id)) continue;
    if (mentionTokenRegex(actor, false).test(text)) actors.push(actor);
  }
  return actors;
}

/**
 * Resolve mention actors for a feed user message so `@tokens` can render as
 * highlighted chips. Uses persisted `mentions` ids + project members first;
 * when ids are missing (pre-deploy API) falls back to matching `@Name` tokens
 * in the message body against `members`.
 */
export function resolveMentionActorsForFeedMessage(
  content: string,
  mentionIds: string[] | undefined,
  members: FeedActorMember[] | undefined,
  mentionEmails?: string[] | undefined,
): MentionActor[] {
  const actors: MentionActor[] = [];
  const ids = mentionIds ?? [];

  for (const id of ids) {
    if (id === PUNTT_MENTION_ID) {
      const actor = { id, name: "Puntt" };
      if (mentionTokenRegex(actor, false).test(content)) actors.push(actor);
      continue;
    }
    const member = members?.find((m) => m._id === id);
    const name = member?.name ?? member?.email;
    if (!name) continue;
    const actor = { id, name };
    if (mentionTokenRegex(actor, false).test(content)) actors.push(actor);
  }

  for (const email of mentionEmails ?? []) {
    const actor = { id: emailMentionId(email), name: email };
    if (
      !actors.some((a) => a.id === actor.id) &&
      mentionTokenRegex(actor, false).test(content)
    ) {
      actors.push(actor);
    }
  }

  if (actors.length > 0) return actors;

  for (const member of members ?? []) {
    const name = member.name ?? member.email;
    if (!name) continue;
    const actor = { id: member._id, name };
    if (
      !actors.some((a) => a.id === actor.id) &&
      mentionTokenRegex(actor, false).test(content)
    ) {
      actors.push(actor);
    }
  }
  const puntt = { id: PUNTT_MENTION_ID, name: "Puntt" };
  if (
    !actors.some((a) => a.id === puntt.id) &&
    mentionTokenRegex(puntt, false).test(content)
  ) {
    actors.push(puntt);
  }
  return actors;
}

/** Keep mention metadata when the server round-trip omits it (stale deploy). */
export function preserveUserMessageMentions<T extends AskPunttMessage>(
  server: T,
  local?: AskPunttMessage | null,
): T {
  if (server.role !== "user") return server;
  const hasServerMentions =
    !!server.mentions?.length || !!server.mentionEmails?.length;
  if (hasServerMentions) return server;
  const hasLocalMentions =
    !!local?.mentions?.length || !!local?.mentionEmails?.length;
  if (!hasLocalMentions) return server;
  return {
    ...server,
    mentions: local?.mentions?.length ? local.mentions : server.mentions,
    mentionEmails: local?.mentionEmails?.length
      ? local.mentionEmails
      : server.mentionEmails,
  };
}

/** Reconcile polled chat history with local user messages (mentions, etc.). */
export function mergeServerMessagesWithLocal(
  serverMessages: AskPunttMessageExt[],
  localMessages: AskPunttMessageExt[],
): AskPunttMessageExt[] {
  const merged = serverMessages.map((serverMsg) => {
    const local = localMessages.find((m) => m._id === serverMsg._id);
    return preserveUserMessageMentions(serverMsg, local) as AskPunttMessageExt;
  });
  const serverIds = new Set(serverMessages.map((m) => m._id));
  const pendingLocal = localMessages.filter((m) => {
    if (serverIds.has(m._id)) return false;
    // Optimistic composer messages use local-* ids until POST succeeds.
    if (m._id.startsWith("local-")) return true;
    // After POST we swap to the real id immediately, but a stale in-flight
    // history refetch can land before the cache includes the new row — keep
    // the confirmed user message visible until the server echo arrives.
    return m.role === "user";
  });
  return pendingLocal.length > 0 ? [...merged, ...pendingLocal] : merged;
}

export type MentionSegment =
  | { type: "text"; text: string }
  | { type: "mention"; id: string; label: string };

/**
 * Split message content into plain-text and mention segments so the feed can
 * highlight `@Name` / `@puntt` tokens as chips. `actors` are the resolved
 * mentions (id + display name) for this message; pass an empty array to render
 * the content as a single text segment.
 */
export function renderMentionSegments(
  content: string,
  actors: MentionActor[],
): MentionSegment[] {
  if (!content) return [];
  if (!actors.length) return [{ type: "text", text: content }];

  const matches: { start: number; end: number; id: string; label: string }[] =
    [];
  for (const actor of actors) {
    const re = mentionTokenRegex(actor, true);
    let m: RegExpExecArray | null;
    while ((m = re.exec(content)) !== null) {
      matches.push({
        start: m.index,
        end: m.index + m[0].length,
        id: actor.id,
        label: m[0],
      });
      // Guard against zero-length matches looping forever.
      if (m.index === re.lastIndex) re.lastIndex++;
    }
  }
  // Earliest first; on a tie the longer match wins so "@Ann Marie" beats "@Ann".
  matches.sort((a, b) => a.start - b.start || b.end - a.end);

  const segments: MentionSegment[] = [];
  let cursor = 0;
  for (const mt of matches) {
    if (mt.start < cursor) continue; // overlaps a chip already emitted
    if (mt.start > cursor) {
      segments.push({ type: "text", text: content.slice(cursor, mt.start) });
    }
    segments.push({ type: "mention", id: mt.id, label: mt.label });
    cursor = mt.end;
  }
  if (cursor < content.length) {
    segments.push({ type: "text", text: content.slice(cursor) });
  }
  return segments;
}

/**
 * Reject a persisted chat-heavy two-pane split for ticket/canvas views (from
 * a legacy 50/50-era persistence or a manual drag) and fall back to the
 * review-biased default. Guards `raw.length < 2` first — a short/malformed
 * persisted array (corrupted localStorage, an old schema) must not read past
 * its end before the ratio check runs.
 */
export function clampTwoPaneLayout(
  raw: readonly number[],
  defaultLayout: readonly number[],
): number[] {
  // The `raw.length < 2` check is load-bearing on its own: without it, a
  // short/corrupt array reads `raw[1]` as `undefined`, and `undefined < 50`
  // evaluates to `false` (not `true`) — so a malformed single-element layout
  // would silently pass the ratio check below and render unclamped instead
  // of falling back to `defaultLayout`.
  if (raw.length < 2 || raw[0] > 45 || raw[1] < 50) {
    return [...defaultLayout];
  }
  return [...raw];
}

/** Human-readable labels for Ask Puntt ``activeSkill`` values in the chat UI. */
export const ASK_PUNTT_SKILL_LABELS: Record<string, string> = {
  claim_generation: "Claims Verification",
  compliance_review: "Compliance Review",
  regulatory_advisor: "Regulatory Advisor",
  artwork_review: "Artwork Review",
  brief_creation: "Brief Writing",
  compare: "Brief Comparison",
  project: "Project",
  puntt_actions: "Puntt Capabilities",
  puntt_value: "Puntt Value",
  meeting_roi: "ROI Workshop",
};
